import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { FavoriteInformerFindInterface } from '@zaoblako/shared/modules/notification/dtos/informer/favorite-informer-find.dto';
import { FavoriteInformerTypeEnum } from '@zaoblako/shared/modules/notification/enums/favorite-informer-type.enum';
import { ProjectKindEnum } from '@zaoblako/shared/modules/project/enum/project.kind.enum';
import { ProjectStatusTypeEnum } from '@zaoblako/shared/modules/project/enum/project.status.type.enum';
import { FavoriteInformerService } from '@zaoblako/web/core/services/notification/favorite-informer.service';
import { HdPrefixService } from '@zaoblako/web/core/services/prefix/hd-prefix.service';
import { NoDataSize } from '@zaoblako/web/shared/components/no-data/constants/no-data-types.enum';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { HtmlTextFormatHelper } from '@zaoblako/web/shared/helpers/html-text-format.helper';
import { ConcatDateParamsPipe } from '@zaoblako/web/shared/pipes/concat-date-params.pipe';
import { KeyValueFilterPipe } from '@zaoblako/web/shared/pipes/key-value-filter.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  standalone: true,
  selector: 'crm-notification-favorites-list',
  templateUrl: './notification-favorites-list.component.html',
  imports: [
    CommonModule,
    OverlayPanelModule,
    ButtonComponent,
    RouterModule,
    ProgressSpinnerModule,
    OrderByPipe,
    StatusLabelComponent,
    TooltipModule,
    NoDataComponent,
    SpinnerComponent,
    KeyValueFilterPipe,
    ConcatDateParamsPipe,
    TruncatePipe,
    TableComponent,
    SafeHtmlPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationFavoritesListComponent {
  public loading = false;
  public paginationLoading = false;
  public notifications: FavoriteInformerFindInterface[] = [];
  public count: number;
  public diff = 0;

  public informerOpen = false;

  public queryParams: PaginationQueryInterface = {
    limit: 20,
    offset: 0
  };

  public NoDataSize: NoDataSize.M;

  public get ProjectStatusTypeEnum() {
    return ProjectStatusTypeEnum;
  }

  public get FavoriteInformerTypeEnum() {
    return FavoriteInformerTypeEnum;
  }

  public get ProjectKindEnum() {
    return ProjectKindEnum;
  }

  public constructor(
    private readonly favoriteInformerService: FavoriteInformerService,
    public readonly hdPrefixService: HdPrefixService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public onInformerShow(): void {
    this.informerOpen = true;
    this.loadNotifications();
    this.cdRef.markForCheck();
  }

  public onInformerHide(): void {
    this.informerOpen = false;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();
  }

  public loadNotifications({ concat } = { concat: false }): void {
    if (!concat) {
      this.loading = true;
      this.cdRef.markForCheck();
    }

    this.favoriteInformerService.getExpiredObjects(this.queryParams).subscribe({
      next: (notifications) => {
        this.notifications = concat ? this.notifications.concat(notifications.items) : notifications.items;
        const filteredNotifications: FavoriteInformerFindInterface[] = [...this.notifications].reduce(
          (acc: FavoriteInformerFindInterface[], notification: FavoriteInformerFindInterface) => {
            if (acc.findIndex((item) => notification.id === item.id && notification.type === item.type) !== -1) {
              return acc;
            }
            return [...acc, notification];
          },
          []
        );
        this.diff = this.diff + (this.notifications.length - filteredNotifications.length);
        this.notifications = filteredNotifications;
        this.count = notifications.count - this.diff;
        this.loading = false;
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public onScrollEnd(target: HTMLElement): void {
    if (target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight) {
      if (this.notifications.length < this.count && !this.loading && !this.paginationLoading) {
        this.paginationLoading = true;
        this.queryParams.offset += this.queryParams.limit;
        this.loadNotifications({ concat: true });
      }
    }
  }

  public getText(description: string): string {
    return HtmlTextFormatHelper.getText(description);
  }
}
